import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MessageSquare, CalendarCheck, ShieldCheck, MapPin, Train, Plane, Landmark,
  Briefcase, GraduationCap, HeartPulse, ShoppingBag,
} from "lucide-react";
import { initLenis } from "../lib/smooth";
import { getSiteSettings, digitsOnly } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/ContactFooter";
import { LeadModal } from "../components/LeadForm";
import { StickyMobileBar, WhatsAppFloat } from "../components/StickyElements";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, SectionHeading } from "../components/Reveal";
import { AGENT_REG, WA_MESSAGE, LOCATION_GROUPS } from "../data/site";

const LOCATION_FAQS = [
  {
    q: "Where is Vinayak 21 Acres located?",
    a: "Vinayak 21 Acres is located at Hatishala — off and adjacent to Newtown Action Area III, Kolkata.",
  },
  {
    q: "Is Vinayak 21 Acres inside Action Area III?",
    a: "No. According to the available project information, the project is at Hatishala, off/adjacent to Newtown Action Area III — not physically inside Action Area III.",
  },
  {
    q: "How far is the nearest metro from Vinayak 21 Acres?",
    a: "The nearest metro is indicatively about 12 minutes away, and a shuttle service to the nearest metro is planned as part of the project's conveniences. Travel times are indicative and may vary depending on traffic and route.",
  },
  {
    q: "How far is the airport from Vinayak 21 Acres?",
    a: "The airport is indicatively about 30 minutes away. Travel times are indicative and may vary depending on traffic and route.",
  },
  {
    q: "Which IT hubs are near Vinayak 21 Acres?",
    a: "Indicative travel times include Infosys about 1 minute, ITC Infotech and Wipro about 2 minutes, Unitech Infospace about 10 minutes and TCS about 12 minutes.",
  },
  {
    q: "Which schools and universities are nearby?",
    a: "Indicatively: Army Institute of Management ~5 mins, Orchids The International School ~7 mins, UEM ~7 mins, St Xavier's University ~7 mins, DPS Newtown and The Newtown School ~15 mins, Sister Nivedita University and Techno International ~15 mins, and Narayana School ~20 mins.",
  },
  {
    q: "Which hospitals are near Vinayak 21 Acres?",
    a: "Wellcare Nursing Home is indicatively under 1 minute away, with Tata Medical Centre ~12 mins, and Neotia Bhagirathi and Ohio Hospital ~15 mins.",
  },
  {
    q: "How can I check current prices, plans or book a site visit?",
    a: "Use the enquiry buttons on this page, WhatsApp us, or call Forest Hill Realtors — we will share the latest developer-provided pricing, floor plans and a convenient site-visit slot.",
  },
];

const GROUP_ICONS = {
  "Major Landmarks": Landmark,
  "Work": Briefcase,
  "Shopping": ShoppingBag,
  "Healthcare": HeartPulse,
  "Education": GraduationCap,
};

function injectJsonLd(id, data) {
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.id = id;
  el.text = JSON.stringify(data);
  document.head.appendChild(el);
}

export default function LocationPage() {
  const [modal, setModal] = useState({ open: false, source: "Location Page Enquiry", interest: "" });
  const [contact, setContact] = useState({ phone: "+919163841785", whatsapp: "+919163841785" });

  useEffect(() => {
    document.title = "Vinayak 21 Acres Location | New Town Kolkata Connectivity";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Explore Vinayak 21 Acres location at Hatishala, off Newtown Action Area III, Kolkata. Check connectivity, nearby landmarks and location advantages."
      );
    injectJsonLd("ld-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://foresthillrealtors.com/" },
        { "@type": "ListItem", position: 2, name: "Vinayak 21 Acres Location", item: "https://foresthillrealtors.com/vinayak-21-acres-location" },
      ],
    });
    injectJsonLd("ld-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: LOCATION_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    initLenis();
    getSiteSettings().then(setContact);
    return () => {
      document.getElementById("ld-breadcrumb")?.remove();
      document.getElementById("ld-faq")?.remove();
    };
  }, []);

  const openModal = (source, interest = "") => setModal({ open: true, source, interest });
  const waLink = `https://wa.me/${digitsOnly(contact.whatsapp)}?text=${encodeURIComponent(WA_MESSAGE)}`;

  return (
    <div className="bg-[#FFFFFF]" data-testid="location-page">
      <Header onBookVisit={() => openModal("Site Visit Booking")} phone={contact.phone} />
      <main>
        <section className="relative bg-[#EAF5D6] pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden hero-grain">
          <div className="absolute inset-0 opacity-[0.14]" aria-hidden="true">
            <img src="/assets/masterplan.jpg" alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="overline-tag" data-testid="locationpage-overline">Location Guide · Forest Hill Realtors</p>
            <h1
              className="font-serif-display text-[#3F6212] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 max-w-4xl"
              data-testid="locationpage-h1"
            >
              Vinayak 21 Acres <span className="text-[#84CC16]">Location & Connectivity</span>
            </h1>
            <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg max-w-2xl leading-relaxed">
              Everything about the Vinayak 21 Acres location — the exact setting at Hatishala, connectivity to New
              Town and Action Area III, and the workplaces, schools, hospitals and conveniences around it, using only
              verified project information.
            </p>
            <div className="mt-8">
              <Link to="/" data-testid="locationpage-home-link"
                className="inline-flex items-center gap-2 text-[#84CC16] font-sub font-semibold text-sm border border-[#84CC16]/50 rounded-full px-6 py-3 hover:bg-[#84CC16]/10 active:scale-[0.98] transition-all duration-300">
                Explore Vinayak 21 Acres <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </section>

        <section data-testid="locationpage-exact-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="01" eyebrow="The Setting" title="Exact Project Location" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Vinayak 21 Acres is located at <strong className="text-[#365314]">Hatishala, Kolkata</strong> — off
                  and adjacent to Newtown Action Area III. The project is next to the Action Area III employment
                  corridor rather than inside it, which places residents beside New Town's workplaces while retaining
                  a more residential setting.
                </p>
                <p>
                  This page is published by Forest Hill Realtors, the authorised channel partner — it is not the
                  official developer website, and all location details should be independently verified before
                  booking.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 rounded-2xl bg-[#FFFFFF] border border-[#DCECC0] overflow-hidden shadow-[0_24px_48px_-28px_rgba(77,124,15,0.25)]" data-testid="locationpage-map-card">
                <iframe
                  title="Vinayak 21 Acres location map — Hatishala, off Newtown Action Area III, Kolkata"
                  src={MAPS.embed}
                  className="w-full h-[320px] sm:h-[400px] border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  data-testid="locationpage-map-embed"
                />
                <div className="p-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#DCECC0]">
                  <p className="text-xs text-[#65A30D] leading-relaxed max-w-xl">
                    Map is indicative of the Hatisala More / Bhojerhat Road area beside Newtown Action Area III —
                    please verify the exact project location before your visit.
                  </p>
                  <a
                    href={MAPS.directions}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="locationpage-directions-link"
                    className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-sub font-semibold text-[#3F6212] border border-[#9CCB58] rounded-full px-4 py-2 hover:bg-[#F4FAE8] transition-all duration-300"
                  >
                    Get Directions
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="locationpage-connectivity-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="02" eyebrow="Getting Around" title="Connectivity with New Town & Action Area III" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Vinayak 21 Acres connectivity centres on short, practical drives: Biswa Bangla Gate — the heart of
                  New Town — is indicatively about 12 minutes away, as is the nearest metro. A shuttle service to the
                  nearest metro is planned as a project convenience. Science City and the airport are indicatively
                  about 30 minutes each.
                </p>
                <p>
                  Because the project sits off Action Area III, the major IT workplaces of the corridor are among the
                  closest destinations of all — several within a few minutes.
                </p>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-3 gap-4 mt-10">
              {[
                { icon: Train, label: "Nearest Metro", time: "~12 mins" },
                { icon: Plane, label: "Airport", time: "~30 mins" },
                { icon: Landmark, label: "Biswa Bangla Gate", time: "~12 mins" },
              ].map((t) => (
                <Reveal key={t.label} delay={0.08}>
                  <div className="bg-white border border-[#DCECC0] rounded-xl p-6 text-center" data-testid={`locationpage-chip-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <t.icon size={20} className="text-[#65A30D] mx-auto" strokeWidth={1.6} />
                    <p className="text-sm text-[#65A30D] mt-3">{t.label}</p>
                    <p className="font-mono-spec text-lg text-[#365314] mt-1">{t.time}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="locationpage-directory-section" className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="03" eyebrow="Around the Project" title="What's Nearby — Verified Travel Times" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-3xl">
                Workplaces, schools, hospitals and daily conveniences around the project, exactly as listed in the
                project information.
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
              {LOCATION_GROUPS.map((group, gi) => {
                const Icon = GROUP_ICONS[group.category] || MapPin;
                return (
                  <Reveal key={group.category} delay={0.05 * gi} className={group.category === "Education" ? "sm:col-span-2 lg:col-span-1" : ""}>
                    <div className="bg-white border border-[#DCECC0] rounded-xl p-6 h-full" data-testid={`locationpage-group-${group.category.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <Icon size={17} className="text-[#65A30D]" strokeWidth={1.7} />
                        <p className="overline-tag">{group.category}</p>
                      </div>
                      <ul className="divide-y divide-[#E3EFCB]">
                        {group.items.map((item) => (
                          <li key={item.name} className="flex items-center justify-between py-2">
                            <span className="text-[13px] text-[#4D7C0F]">{item.name}</span>
                            <span className="font-mono-spec text-[12px] text-[#365314] shrink-0 ml-3">{item.time}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={0.1}>
              <p className="text-xs text-[#65A30D] mt-8" data-testid="locationpage-travel-disclaimer">
                Travel times are indicative and may vary depending on traffic and route.
              </p>
            </Reveal>
          </div>
        </section>

        <section data-testid="locationpage-why-section" className="py-16 sm:py-24 bg-[#EAF5D6]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="04" eyebrow="For Homebuyers" title="Why This Location May Work for You" dark />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  The Vinayak 21 Acres Hatishala setting may suit buyers who work in the Action Area III IT corridor
                  and want a short daily commute, families who value having schools, universities and hospitals within
                  a 5–20 minute band, and households that prefer everyday shopping within minutes rather than across
                  town.
                </p>
                <p>
                  As with any location, we recommend visiting at your actual commute hours and verifying what matters
                  most to your routine before deciding.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-wrap gap-3">
                <button data-testid="locationpage-enquire-btn" onClick={() => openModal("Enquire About Vinayak 21 Acres")}
                  className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-8 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                  Enquire About Vinayak 21 Acres
                </button>
                <a data-testid="locationpage-whatsapp-btn" href={waLink} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white font-sub font-bold text-sm px-7 py-4 rounded-full hover:bg-[#1EBE57] active:scale-[0.98] transition-all duration-300">
                  <MessageSquare size={16} /> WhatsApp Us
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="locationpage-links-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <SectionHeading index="05" eyebrow="Keep Exploring" title="Plans, Prices & the Neighbourhood" center />
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap justify-center gap-3">
                <Link to="/vinayak-21-acres-floor-plan" data-testid="locationpage-floorplan-link"
                  className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                  Vinayak 21 Acres Floor Plans <ArrowRight size={15} />
                </Link>
                <Link to="/vinayak-21-acres-price" data-testid="locationpage-price-link"
                  className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                  Vinayak 21 Acres Price <ArrowRight size={15} />
                </Link>
                <Link to="/flats-near-new-town-action-area-3" data-testid="locationpage-flats-link"
                  className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                  Flats Near New Town Action Area 3 <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="locationpage-visit-section" className="pb-16 sm:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl bg-[#EAF5D6] p-8 sm:p-12 text-center hero-grain">
                <p className="overline-tag">06 · Visit</p>
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3F6212] leading-tight mt-4">
                  Book a Site Visit
                </h2>
                <p className="text-[#4D7C0F] text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
                  Judge the location the reliable way — drive the commute, walk the neighbourhood and see the project
                  in person.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
                  <button data-testid="locationpage-book-visit-btn" onClick={() => openModal("Site Visit Booking")}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    <CalendarCheck size={16} /> Book a Site Visit
                  </button>
                  <a data-testid="locationpage-whatsapp-cta" href={waLink} target="_blank" rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-sub font-bold text-sm px-7 py-4 rounded-full hover:bg-[#1EBE57] active:scale-[0.98] transition-all duration-300">
                    <MessageSquare size={16} /> WhatsApp Us
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="locationpage-faq-section" className="pb-16 sm:pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="07" eyebrow="FAQs" title="Location FAQs" center />
            <Reveal delay={0.15}>
              <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="locationpage-faq-accordion">
                {LOCATION_FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={f.q} data-testid={`locationpage-faq-item-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl px-6 data-[state=open]:border-[#84CC16] transition-colors duration-300">
                    <AccordionTrigger data-testid={`locationpage-faq-trigger-${i}`}
                      className="text-left font-sub font-medium text-[15px] text-[#365314] hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-sm text-[#4D7C0F] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <div className="bg-[#EAF5D6] border-t border-[#DCECC0] py-7" data-testid="locationpage-partner-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <ShieldCheck size={17} className="text-[#84CC16] shrink-0" />
            <p className="text-[#4D7C0F] text-xs sm:text-[13px] leading-relaxed">
              <span className="text-[#3F6212] font-sub font-semibold">Forest Hill Realtors</span> · Authorised Channel
              Partner · WBRERA Agent Registration: <span className="font-mono-spec">{AGENT_REG}</span> · Not the
              official developer website
            </p>
          </div>
        </div>

        <Footer />
      </main>
      <LeadModal open={modal.open} onClose={() => setModal((m) => ({ ...m, open: false }))} source={modal.source} defaultInterest={modal.interest} whatsapp={contact.whatsapp} />
      <StickyMobileBar phone={contact.phone} whatsapp={contact.whatsapp} onEnquire={() => openModal("Quick Enquiry")} />
      <WhatsAppFloat whatsapp={contact.whatsapp} />
    </div>
  );
}
